import {
  Box,
  Button,
  Icon,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSection,
  TabsViewIcon,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import { useCallback } from "react";

import {
  ModuleMenu,
  SidebarCountBadge,
  type ModuleMenuSection,
} from "#app/components/navigation/module-menu";
import { useSidebarNav } from "#app/components/navigation/use-sidebar-nav";
import { EntityTag } from "#app/components/entity";
import { renderBasicEntityMenuItems } from "#app/components/menus/basic-entity-menu-items";
import { FavoriteItem } from "#app/components/navigation/favorite-item";
import { useAppData } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";
import { DashboardActionsProvider, useDashboardActions } from "./dashboard-actions";
import { useDashboardFavorites } from "./use-dashboard-favorites";
import { AnalyticsContext, DashboardEvent, trackEvent } from "#app/lib/analytics";
import { surfaceOwnedBy, useDashboardSurfaces } from "./use-dashboard-surfaces";
import { useRecentDashboards } from "./use-recent-dashboards";
import type { ResolvedDashboardRef } from "./use-dashboard-surfaces";
import { workspaceHref } from "#app/lib/utils/entity-hrefs";

/**
 * Dashboards' primary destinations: the whole directory, and the owned-by-me
 * slice of it. "My Dashboards" carries its count in the badge — the same
 * directory metadata All Teams/All Members show, and what tells the viewer
 * whether the slice has anything in it before they press.
 */
function DashboardsNavSection() {
  const { navigate, isActive } = useSidebarNav();
  const { currentUser } = useAppData();
  const { surfaces } = useDashboardSurfaces();

  const myDashboardCount = surfaces.filter((surface) =>
    surfaceOwnedBy(surface, currentUser?.userId),
  ).length;

  return (
    <SidebarGroup>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            isActive={isActive("/dashboards", true)}
            onClick={() => navigate("/dashboards")}
          >
            <Icon name="bar-chart" />
            <Text truncate>All Dashboards</Text>
          </SidebarMenuButton>
        </SidebarMenuItem>
        <SidebarMenuItem>
          <SidebarMenuButton
            isActive={isActive("/dashboards/my")}
            onClick={() => navigate("/dashboards/my")}
          >
            {/* The viewer's own avatar is the glyph — "my" is this user, not a
              * generic person mark (the CurrentUserButton read). Default size:
              * its 20px circle IS the row's reserved leading-visual column
              * (`--sidebar-row-visual` = AVATAR_SIZE_PX.xs), so the glyph
              * centerline and the label land exactly where the sibling rows'
              * icons put them. A smaller circle breaks both: the chip shell's
              * inline `min-width: 0` beats the button's column reservation,
              * so the row collapses to the circle and the label slides left. */}
            <EntityTag
              entity={{
                kind: "user",
                id: currentUser?.userId ?? "me",
                user: currentUser ?? null,
              }}
              display="visual"
              interactive={false}
            />
            <Text truncate>My Dashboards</Text>
          </SidebarMenuButton>
          <SidebarCountBadge count={myDashboardCount} />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );
}

/**
 * One dashboard row — the dashboard's OWN identity (the view's name and its
 * colored icon, the same three its host's tab strip shows), never the host
 * entity's visual. `TabsViewIcon` matches the sibling rows' icon footprint,
 * so the label lands on the shared leading-visual column.
 *
 * The ⋯ and right-click menus are the dashboard roster
 * (`renderDashboardMenuItems`, through `useDashboardActions`): Favorite /
 * Unfavorite, Copy link, Rename, Duplicate, Delete, Sharing — the same
 * actions the directory table's rows answer with, so a dashboard offers one
 * menu everywhere. The Recent rows carry it too: the star there is how a
 * recent becomes a favorite without a trip to the directory. Before the
 * surface resolves (the first frame's stored mirror), the row falls back to
 * the basic entity roster — there is no row to rename or delete yet.
 */
function DashboardRow({ dashboard }: { dashboard: ResolvedDashboardRef }) {
  const { navigate, isActive } = useSidebarNav();
  const { isFavorite, toggleFavorite, removeFavorite } = useDashboardFavorites();
  const { surfaceByRefKey } = useDashboardSurfaces();
  const { renderMenuItems } = useDashboardActions();
  const dashboardUrl = useDashboardUrl();
  const favorited = isFavorite(dashboard.refKey);
  const surface = surfaceByRefKey.get(dashboard.refKey);

  // Favoriting snapshots the LIVE surface (name, icon, color, route), so a
  // Recent row favorites through the surface its ref resolves to; unfavoriting
  // needs only the key. A ref that has not resolved yet (the first frame's
  // mirror) has nothing to snapshot and the press is a no-op until it does.
  const handleFavorite = () => {
    if (favorited) {
      trackEvent({
        context: AnalyticsContext.Dashboard,
        event: DashboardEvent.Unfavorite,
        data: { via: "module_menu", ...(surface ? { scope: surface.scope.kind } : {}) },
      });
      removeFavorite(dashboard.refKey);
      return;
    }
    if (surface) toggleFavorite(surface, "module_menu");
  };

  return (
    <FavoriteItem
      actionLabel="Dashboard actions"
      id={dashboard.refKey}
      isActive={isActive(dashboard.route)}
      items={(parts) =>
        surface
          ? renderMenuItems(surface, parts)
          : renderBasicEntityMenuItems({
              href: dashboardUrl(dashboard),
              isFavorited: favorited,
              onFavorite: handleFavorite,
              parts,
            })
      }
      name={dashboard.name}
      onClick={() => navigate(dashboard.route)}
      visual={<TabsViewIcon color={dashboard.color} icon={dashboard.icon} />}
    />
  );
}

/** The absolute URL a dashboard row's link actions open and copy. */
function useDashboardUrl() {
  const { activeWorkspace } = useWorkspace();
  const workspacePath = activeWorkspace?.urlPath ?? "";
  return useCallback(
    (dashboard: ResolvedDashboardRef) => workspaceHref(workspacePath, dashboard.route),
    [workspacePath],
  );
}

/**
 * "Favorites" section — always present, unlike the drop-when-empty content
 * sections: the empty placeholder advertises the star affordance the slot is
 * reserved for (the star lives on the All Dashboards rows).
 */
function DashboardFavoritesSection() {
  const { favorites } = useDashboardFavorites();

  if (favorites.length === 0) {
    return (
      <SidebarSection title="Favorites">
        {/* A card object, not a row: the hint describes the section, so it
          * wears container chrome instead of impersonating a pressable row.
          * `sunken` on purpose despite the small-container guidance — an empty
          * well is the one small box that WANTS the recessed read, and the
          * deeper-than-pane fill is what makes the card read as a reserved
          * slot rather than content. */}
        <Box
          align="center"
          border
          direction="column"
          gap="sm"
          justify="center"
          // Air under the "Favorites" label — the reference card carries an
          // 8px block margin of its own on top of the section header's rhythm.
          marginTop="sm"
          minHeight={88}
          padding="md"
          rounded="lg"
          surface="sunken"
        >
          {/* `warning` is the favorite star's gold everywhere it is lit
            * (global search's favorited rows). */}
          <Icon color="warning" name="star" size="md" />
          <Text align="center" size="xs" variant="muted">
            Star a dashboard to see it here
          </Text>
        </Box>
      </SidebarSection>
    );
  }

  return (
    <SidebarSection title="Favorites">
      {favorites.map((dashboard) => (
        <DashboardRow key={dashboard.refKey} dashboard={dashboard} />
      ))}
    </SidebarSection>
  );
}

/**
 * "Recent" section — the viewer's last-opened dashboards, newest first, each
 * wearing the dashboard's own identity. The stored snapshot is only the
 * first-paint fallback; the live view row wins once it resolves, so renames
 * and recolors reach here on the menu's next mount.
 */
function RecentDashboardsSection() {
  const { recents } = useRecentDashboards();

  // Every module-menu section drops out while it has nothing to show. The
  // menu drops this one from its section list too — that is what takes the
  // divider with it — but the guard keeps the component honest on its own.
  if (recents.length === 0) return null;

  return (
    <SidebarSection title="Recent">
      {recents.map((dashboard) => (
        <DashboardRow key={dashboard.refKey} dashboard={dashboard} />
      ))}
    </SidebarSection>
  );
}

/**
 * Favorites and Recent share ONE menu section so no divider lands between
 * them — `ModuleMenu` draws a hairline before every section it is given, and
 * these two read as one "your dashboards" region. Recent dropping itself
 * while empty is safe inside a shared slot: there is no menu-drawn divider
 * under it to strand.
 */
function DashboardListsSection() {
  return (
    <>
      <DashboardFavoritesSection />
      <RecentDashboardsSection />
    </>
  );
}

const SECTIONS: ModuleMenuSection[] = [
  { id: "nav", Component: DashboardsNavSection },
  { id: "lists", Component: DashboardListsSection },
];

/**
 * The Dashboards module menu. The header's create action is the standard
 * list-append plus in the same chrome as every other module's create control;
 * it opens the same Create Dashboard dialog as the directory's page-header
 * button — name and workflows first, then the row exists and the page lands
 * on its template chooser. On compact the drawer closes before the dialog
 * opens (the Workflows menu's create grammar), so the dialog and the page it
 * lands on are never behind the pane that opened them.
 */
export function DashboardsModuleMenu() {
  const closeSidebar = useCloseSidebarIfMobile();
  const { openNewDashboard } = useNavDialogActions();
  return (
    <DashboardActionsProvider>
      <ModuleMenu
        createMenu={
          // The shadow rides a pill wrapper matching the Button — same
          // construction (and reasons) as the CreateMenuPopover trigger.
          <Box direction="row" rounded="full" shadow="control">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    aria-label="Create Dashboard"
                    size="sm"
                    variant="outline"
                    // Light: no border — the `control` shadow carries the edge.
                    className="not-dark:border-transparent!"
                    onClick={() => {
                      closeSidebar();
                      openNewDashboard();
                    }}
                  />
                }
              >
                <Icon name="plus" size="sm" className="opacity-100" />
              </TooltipTrigger>
              <TooltipPopup side="top">Create Dashboard</TooltipPopup>
            </Tooltip>
          </Box>
        }
        sections={SECTIONS}
        title="Dashboards"
      />
    </DashboardActionsProvider>
  );
}
